<script setup lang="ts">
import DiagnoseLink from './DiagnoseLink.vue';
import InterviewTitle from './InterviewTitle.vue';
import UserInputText from 'theme/components/molecules/UserInputText.vue';
import InterviewCard from '~/components/organisms/InterviewCard/InterviewCard.vue';

const props = defineProps<{ interviewId: InterviewId }>();

const component1 = useLazyComponent(
  async () => (await import('./content/Content1.vue')).default
);
const component2 = useLazyComponent(
  async () => (await import('./content/Content2.vue')).default
);
const component3 = useLazyComponent(
  async () => (await import('./content/Content3.vue')).default
);
const component4 = useLazyComponent(
  async () => (await import('./content/Content4.vue')).default
);

const componentLoader = computed(() => {
  switch (props.interviewId) {
    case '1':
      return component1;
    case '2':
      return component2;
    case '3':
      return component3;
    case '4':
      return component4;
  }
});

await componentLoader.value.loadImmediately();
const contentComponent = computed<Component<{ interviewee: Interviewee }>>(
  () => componentLoader.value.component.value
);

const interview = useInterviewSummary(() => props.interviewId);

const interviews = useInterviewSummaries(() =>
  (['1', '2', '3', '4'] as const).filter((id) => id !== props.interviewId)
);

useAppMeta({
  title: () => interview.value.seoMeta.title,
  description: () => interview.value.seoMeta.description,
  imagePath: () => interview.value.seoMeta.ogImage,
});
</script>

<template>
  <div :class="$style.wrapper">
    <InterviewTitle :topics="[...interview.categories]"
      ><UserInputText :text="interview.title"
    /></InterviewTitle>
    <contentComponent :interviewee="interview.interviewee" />
    <div :class="$style.cards">
      <h2 :class="$style.cards__heading">体験談をもっとみてみよう</h2>
      <div :class="$style.cards__row">
        <InterviewCard
          v-for="i in interviews"
          :id="i.id"
          :key="i.id"
          variant="simple"
        />
      </div>
    </div>
    <DiagnoseLink />
  </div>
</template>

<style module lang="scss">
.wrapper {
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @include mobile-only {
    padding: 40px 16px 24px;
    gap: 24px;
  }
}

.cards {
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  &__heading {
    font-size: 28px;
    line-height: 1.8;
    font-weight: bold;
    margin-top: 16px;
  }

  &__row {
    display: flex;
    gap: 16px;

    @include mobile-only {
      flex-direction: column;
      gap: 8px;
    }

    & > * {
      flex: 1;
    }
  }
}
</style>
